/**
 * Keeps a removed node's generated assets listed in the Download Assets modal.
 *
 * The modal is not a record of what was generated — it is a PROJECTION of the
 * nodes currently on the canvas. `ClipDownloadModal` renders
 * `buildDownloadCandidates(canvasNodes)`, which walks the live node list and
 * pulls each node's asset. So the moment undo removes a node, its image leaves
 * the modal, and to the user the image is simply gone.
 *
 * The bytes are not gone. Node removal touches YDoc only — neither
 * `GraphManager.remove_node` nor the frontend `removeNodeFromYDoc` calls into
 * storage, nothing on that path calls `delete_asset`, and there is no orphan
 * sweep. The file is still in GCS with nothing pointing at it. This module is
 * that missing pointer.
 *
 * SCOPE, deliberately: this is session memory, not a durable index. It lives in
 * a module-level map and dies with the tab. The durable version would read
 * completed `generation_jobs` rows (they already carry project_id, canvas_id,
 * node_id and outputs) and would additionally survive reload and cover
 * intentional deletes — that was considered and deferred.
 *
 * Bucketed per canvas room for the same reason the undo history is
 * (T289182224): a project has several canvases, `WorkspacePageV2` stays mounted
 * across tab switches, and canvas A's assets must not surface in canvas B's
 * modal. `canvasRoomKeyOf` is exported so the page and the modal cannot derive
 * the key differently — a mismatch would not throw, it would just silently
 * list nothing.
 */

import type { CanvasAssetCandidate } from './nodes/shared/mentionUtils'

/**
 * Cap per room. Each entry is a small descriptor, not image bytes, but the map
 * is never pruned within a session and undo/redo can churn a lot of nodes.
 * Oldest entries are dropped first.
 */
export const MAX_REMEMBERED_PER_ROOM = 200

/**
 * Re-exported so `ClipDownloadModal` can derive the same key without importing
 * from `pages/`. The single definition lives with the undo history, which is
 * the other consumer and the one that fails loud on an empty segment.
 */
export { canvasRoomKeyOf } from '../../pages/workspaceUndoHistory'

// Insertion order doubles as age order, so dropping the oldest is a shift of
// the first key.
const byRoom = new Map<string, Map<string, CanvasAssetCandidate>>()

function bucketFor(roomKey: string): Map<string, CanvasAssetCandidate> {
  const existing = byRoom.get(roomKey)
  if (existing) return existing
  const created = new Map<string, CanvasAssetCandidate>()
  byRoom.set(roomKey, created)
  return created
}

/**
 * Remember the assets of nodes that are about to be removed.
 *
 * Keyed by `assetPath` rather than `nodeId`: the same asset can be re-added
 * under a fresh node id (paste, duplicate, redo of a create), and the modal
 * de-dupes on the path, so keying on the path keeps one entry per file.
 */
export function rememberRemovedAssets(
  roomKey: string,
  candidates: readonly CanvasAssetCandidate[],
): void {
  if (candidates.length === 0) return
  const bucket = bucketFor(roomKey)
  for (const candidate of candidates) {
    if (!candidate.assetPath) continue
    // Delete-then-set so a re-remembered asset moves to the young end.
    bucket.delete(candidate.assetPath)
    bucket.set(candidate.assetPath, candidate)
  }
  while (bucket.size > MAX_REMEMBERED_PER_ROOM) {
    const oldest = bucket.keys().next()
    if (oldest.done) break
    bucket.delete(oldest.value)
  }
}

/**
 * Assets remembered for `roomKey`, oldest first.
 *
 * Callers merge these BEHIND the live node candidates and drop any whose
 * `assetPath` is already live — that is what makes redo self-healing: the
 * restored node's own candidate wins and no duplicate row appears.
 */
export function listRemovedAssets(roomKey: string): CanvasAssetCandidate[] {
  return Array.from(byRoom.get(roomKey)?.values() ?? [])
}

/** Test seam. Clears one room, or every room when called with no argument. */
export function clearRemovedAssets(roomKey?: string): void {
  if (roomKey === undefined) byRoom.clear()
  else byRoom.delete(roomKey)
}
